import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';

import { Overlay } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UploadPreview } from '@bingads-webui-campaign-react/upload-file-picker';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { AddMediaSourcesContainer } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { validateImageSizeAsync } from '@bingads-webui-campaign/image-experience-utils';
import { ROOT_FOLDER_ID, FolderAssetType } from '@bingads-webui-campaign-react/asset-library-model';
import {
  AssetLibraryAIGCPromotionBanner,
  AssetLibraryNewFeatureBanner,
} from '@bingads-webui-campaign-react/asset-library-components';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';

import { getLandingPageStyles } from './styles';
import { AssetLibraryImagesView } from './asset-library-images-view';
import { VIEWS } from '../../view-type';

const overlayStyles = {
  root: { zIndex: 2 },
};

export const AddMediaLandingPage = ({
  goToView,
  setHideSubtitle,
  maxUploadNum,
  minSize,
  processUploadedImage,
  onProcessUploadComplete,
  buttonConfig,
  assetLibraryConfig,
  setErrors,
  i18n,
  perfMarker,
  setShowApplyButton,
  setDisableApplyButton,
  triggerAddImagesRef,
  assetLibraryDataService,
  imageExperienceProps,
  imageType,
  setSelectionInfoText,
  allowedAssetType,
  isAssetLibraryAIGCMVPEnabled,
  logger,
  isClickableOutsideFocusTrap,
  disabledAssetSources,
  eventHandler,
  supportImageUpscale,
  minUploadSize,
  showPromotionBanner,
  isAdStudioEnabled,
}) => {
  const classes = useDefaultStyles(getLandingPageStyles);
  const [uploadPreviewImageArr, setUploadPreviewImageArr] = useState([]);
  const [showUploadPreview, setShowUploadPreview] = useState(false);
  const [isSpinnerVisible, showSpinner] = useState(false);

  const {
    onImageSelected: onImgSelected,
    onImagesSelected: onImgsSelected,
    ...restConfig
  } = assetLibraryConfig;

  const {
    isMultimediaAd, imageOption, isBrandKit, isMMAV2Enabled, isDisplayAds, isPortraitImage, isStoreAppProng, isStoreAppSpotLight, isStoreAppTransparentLogo,
  } = imageExperienceProps;

  // tie only to enable folders for now, may disable if MT API dependency integration
  // causes issues
  const enableMultiSelection = true;

  // show spinner when image is selected
  const onImageSelected = useCallback((image) => {
    if (onImgSelected) {
      const imageSelectPerfMarker = perfMarker.createChild('existingImageSelect');
      imageSelectPerfMarker.willInit();

      showSpinner(true);
      onImgSelected(image).finally((imgObj) => {
        let imagePerfMarkerData;

        if (imgObj) {
          imagePerfMarkerData = {
            imageWidth: imgObj.Width,
            imageHeight: imgObj.Height,
          };
        }

        showSpinner(false);
        imageSelectPerfMarker.done(imagePerfMarkerData);
      });
    }
  }, [onImgSelected, perfMarker]);

  const onImagesSelected = useCallback((imageIds) => {
    if (onImgsSelected) {
      const imageSelectPerfMarker = perfMarker.createChild('existingImageSelect');
      imageSelectPerfMarker.willInit();

      showSpinner(true);
      onImgsSelected(imageIds).finally(() => {
        let imagePerfMarkerData;

        showSpinner(false);
        imageSelectPerfMarker.done(imagePerfMarkerData);
      });
    }
  }, [onImgsSelected, perfMarker]);

  const onFailedToLoadAssetLibraryImages = useCallback(() => {
    // tdo: show user error message
  }, []);

  const uploadMediaConfig = useMemo(() => {
    const config = {
      maxUploadNum,
      minSize,
      minUploadSize,
      processUploadedMedia: processUploadedImage,
      onProcessUploadComplete,
      // eslint-disable-next-line arrow-body-style
      uploadMedia: ({ imageData, updateCurrentProgress, name }) => {
        const isPortraitImageEnabled = isPortraitImage || imageOption === constants.imageOptionKeys.Portrait;

        const validateAsync = supportImageUpscale ? Promise.resolve() : validateImageSizeAsync(
          imageData,
          imageType,
          isMultimediaAd,
          imageOption,
          isMMAV2Enabled,
          false,
          isDisplayAds,
          isPortraitImageEnabled,
          isStoreAppProng,
          isStoreAppSpotLight,
          isStoreAppTransparentLogo,
          false,
          false,
          isBrandKit
        );

        return validateAsync
          .then(() => assetLibraryDataService.uploadImage({
            imageData,
            updateCurrentProgress,
            name,
            folderId: ROOT_FOLDER_ID,
            supportUpscale: supportImageUpscale ? true : undefined,
          }))
          .then((imageId) => {
            if (eventHandler && eventHandler.onUploadImage) {
              eventHandler.onUploadImage(imageId);
            }
            return { imageId };
          });
      },
    };

    return config;
  }, [maxUploadNum, minSize, minUploadSize, processUploadedImage, onProcessUploadComplete, isPortraitImage, imageOption, imageType, isMultimediaAd, isMMAV2Enabled, isDisplayAds, isStoreAppProng, isStoreAppSpotLight, isStoreAppTransparentLogo, assetLibraryDataService, supportImageUpscale, eventHandler]);

  const onGenerateImagesClick = useCallback(() => {
    logger.logTrace('Generate images button in banner is clicked.');
    goToView(VIEWS.AIGCImage);
  }, [goToView, logger]);

  return (
    <div className={classes.landingContent}>
      {
        isSpinnerVisible &&
        <Overlay isDarkThemed={false} styles={overlayStyles}>
          <AssetLibrarySpinner useCustomSize />
        </Overlay>
      }
      {
        showUploadPreview ? (
          <React.Fragment>
            <UploadPreview
              assetList={uploadPreviewImageArr}
              maxUploadNum={maxUploadNum}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            {isAssetLibraryAIGCMVPEnabled && showPromotionBanner && (
              <AssetLibraryAIGCPromotionBanner
                onGenerateImagesClick={onGenerateImagesClick}
                i18n={i18n}
              />
            )}
            {!isAssetLibraryAIGCMVPEnabled && (
              <AssetLibraryNewFeatureBanner
                content={isAdStudioEnabled ? i18n.getString(_TL_('Ads Studio is your one-stop shop to view, add, and edit assets across your campaigns and ad groups.')) :
                  i18n.getString(_TL_('Asset library is your one-stop shop to view, add, and edit assets across your campaigns and ad groups.'))}
                i18n={i18n}
              />
            )}
            <AddMediaSourcesContainer
              goToView={goToView}
              setUploadPreviewMediaArr={setUploadPreviewImageArr}
              setShowUploadPreview={setShowUploadPreview}
              setUploadErrors={setErrors}
              uploadMediaConfig={uploadMediaConfig}
              setHideSubtitle={setHideSubtitle}
              buttonConfig={buttonConfig}
              logger={logger}
              assetLibraryConfig={assetLibraryConfig}
              supportImageUpscale={supportImageUpscale}
            />
            <div className={classes.assetLibraryGridContainer} data-is-scrollable>
              <AssetLibraryImagesView
                onImageSelected={onImageSelected}
                onFailedToLoadImages={onFailedToLoadAssetLibraryImages}
                i18n={i18n}
                multiSelectionEnabled={enableMultiSelection}
                setShowApplyButton={setShowApplyButton}
                onImagesSelected={onImagesSelected}
                triggerAddImagesRef={triggerAddImagesRef}
                maxUploadNum={maxUploadNum}
                setErrors={setErrors}
                setDisableApplyButton={setDisableApplyButton}
                dataService={assetLibraryDataService}
                setSelectionInfoText={setSelectionInfoText}
                allowedAssetType={allowedAssetType}
                logger={logger}
                isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
                isClickableOutsideFocusTrap={isClickableOutsideFocusTrap}
                disabledAssetSources={disabledAssetSources}
                {...restConfig}
              />
            </div>
          </React.Fragment>
        )
      }
    </div>
  );
};

AddMediaLandingPage.propTypes = {
  goToView: PropTypes.func.isRequired,
  setHideSubtitle: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  processUploadedImage: PropTypes.func.isRequired,
  onProcessUploadComplete: PropTypes.func.isRequired,
  buttonConfig: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
  })).isRequired,
  assetLibraryConfig: PropTypes.shape({
    aid: PropTypes.string,
    cid: PropTypes.string,
    dateFormat: PropTypes.string,
    timeZone: PropTypes.number,
    oDataConfig: PropTypes.shape({}),
    preferencesService: PropTypes.shape({}),
    onImageSelected: PropTypes.func,
    onImagesSelected: PropTypes.func,
  }).isRequired,
  showPromotionBanner: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setErrors: PropTypes.func.isRequired,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  setShowApplyButton: PropTypes.func.isRequired,
  triggerAddImagesRef: PropTypes.shape({}).isRequired,
  setDisableApplyButton: PropTypes.func.isRequired,
  assetLibraryDataService: PropTypes.shape({}).isRequired,
  imageExperienceProps: PropTypes.shape({}).isRequired,
  imageType: PropTypes.string.isRequired,
  setSelectionInfoText: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  allowedAssetType: PropTypes.string,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isClickableOutsideFocusTrap: PropTypes.bool,
  disabledAssetSources: PropTypes.arrayOf(PropTypes.string),
  eventHandler: PropTypes.shape({}),
  supportImageUpscale: PropTypes.bool,
  minUploadSize: PropTypes.arrayOf(PropTypes.number),
};

AddMediaLandingPage.defaultProps = {
  allowedAssetType: FolderAssetType.Image,
  isAssetLibraryAIGCMVPEnabled: false,
  isClickableOutsideFocusTrap: false,
  disabledAssetSources: [],
  eventHandler: null,
  showPromotionBanner: true,
  supportImageUpscale: false,
  minUploadSize: [],
};
